import { z } from 'zod';
import { VENDOR_PRODUCT_CATEGORY_CODES } from './vendor-profile';

export const SUPPLIER_ASSESSMENT_VERSION = 'WS_SUPPLIER_ASSESSMENT_V1';
export const ASSESSMENT_SECTIONS = {
  '1': '基本證照',
  '2': '品質系統',
  '3': '管理責任',
  '4': '倉儲系統',
  '5': '運輸系統',
  '6': '其他',
} as const;
export type AssessmentSection = keyof typeof ASSESSMENT_SECTIONS;
export type Applicability = 'REQUIRED' | 'OPTIONAL' | 'NOT_APPLICABLE';
export const QUALIFICATION_LABELS = {
  DRUG_DEALER: '藥商',
  DRUG_MANUFACTURER: '藥廠',
  TRANSPORTER: '運送業者',
  GENERAL_SUPPLIER: '一般商品供應商',
  OTHER: '其他',
} as const;
export const DOCUMENT_LABELS = {
  DRUG_DEALER_LICENSE: '藥商許可執照',
  DRUG_MANUFACTURER_LICENSE: '藥廠許可／製造證照',
  CONTROLLED_DRUG_CERTIFICATE: '管制藥品登記證',
  BUSINESS_REGISTRATION: '公司營業登記證明',
  PICS_GMP_CERTIFICATE: '藥品優良製造證明書',
  GDP_APPROVAL: 'GDP核准函',
  ISO_CERTIFICATE: 'ISO證書',
  ORGANIZATION_CHART: '人事組織圖',
  OTHER_CERTIFICATE: '其他認證證書',
  PERSONNEL_CERTIFICATE: '人員證照',
  COMPANY_SEAL: '公司章／公司行號章',
  OTHER: '其他文件',
} as const;
export type SupplierDocumentType = keyof typeof DOCUMENT_LABELS;
export type AssessmentAnswer = 'YES' | 'NO' | 'NOT_APPLICABLE';
export interface AssessmentQuestion {
  question_code: string;
  section_code: AssessmentSection;
  question_text: string;
  applicability_rule: string;
  required_document_type?: SupplierDocumentType;
  requires_description?: boolean;
}

const question = (
  code: string,
  text: string,
  rule = 'PHARMACEUTICAL',
  document?: SupplierDocumentType,
  description = false,
): AssessmentQuestion => ({
  question_code: code,
  section_code: code[0] as AssessmentSection,
  question_text: text,
  applicability_rule: rule,
  required_document_type: document,
  requires_description: description,
});

// 題目編號以文字保存，避免 Excel 2.10 被轉成 2.1。發布後修改須新增版本。
export const SUPPLIER_ASSESSMENT_QUESTIONS: readonly AssessmentQuestion[] = [
  question(
    '1.1',
    '是否具有有效的「製造業／販賣業藥商許可執照」？',
    'PHARMACEUTICAL',
    'DRUG_DEALER_LICENSE',
  ),
  question(
    '1.2',
    '是否具有有效的「管制藥品登記證」？',
    'PHARMACEUTICAL',
    'CONTROLLED_DRUG_CERTIFICATE',
  ),
  question('1.3', '是否完成公司營業登記？', 'ALL', 'BUSINESS_REGISTRATION'),
  question(
    '2.1',
    '藥品之製造廠是否通過「PIC/S GMP」？非藥廠可選不適用。',
    'MANUFACTURER',
    'PICS_GMP_CERTIFICATE',
  ),
  question('2.2', '是否已取得「藥品GDP」？', 'PHARMACEUTICAL', 'GDP_APPROVAL'),
  question('2.3', '是否通過ISO認證？', 'PHARMACEUTICAL', 'ISO_CERTIFICATE'),
  question('2.4', '是否通過其他認證？', 'PHARMACEUTICAL', 'OTHER_CERTIFICATE', true),
  question('2.5', '是否制定人員教育訓練計畫及考核？'),
  question('2.6', '是否有建立品質管理組織架構？'),
  question('2.7', '文件是否有標準化？'),
  question('2.8', '若品質發生異常，是否有偏差調查或處理之標準程序？'),
  question(
    '2.9',
    '若公司品質系統有重大變更，是否於變更執行前通知惠生？包括認證狀態、所有權、廠址、公司負責人或管理代表變更。',
  ),
  question('2.10', '藥品製程若有重大變更或發生不良品回收事件時，是否會主動告知？'),
  question('2.11', '廠內標準作業程序是否皆有適當工作指導書？'),
  question('2.12', '是否具有變更管制措施？'),
  question('2.13', '是否具有批次製造紀錄？'),
  question('2.14', '是否有留樣品？'),
  question('3.1', '是否提供「人事組織圖」？', 'PHARMACEUTICAL', 'ORGANIZATION_CHART'),
  question('3.2', '是否明訂各部門與人員之職責內容？'),
  question('3.3', '是否制訂「代理人制度」？'),
  question('3.4', '若與惠生對口人員有調整或調動，是否提前告知並完整交接相關工作事項？'),
  question('4.1', '是否具備符合攝氏2～8℃之冷鏈藥品儲存場所？'),
  question('4.2', '是否設立室溫攝氏15～25℃之儲存場所？'),
  question('4.3', '儲存藥品場所是否具備溫度連續紀錄裝置及警報系統？'),
  question('4.4', '是否有不斷電系統及空調備援系統？'),
  question('4.5', '是否有空調備援系統，如遇斷電可移至備用冷藏空間？'),
  question('4.6', 'GDP關鍵設備是否皆已完成驗證確效？'),
  question('4.7', '廠內設備是否定期維護或校正？'),
  question('4.8', '如有校正，校正標準是否可追溯至國家標準？', 'CALIBRATION_YES'),
  question('4.9', '是否有門禁管理？'),
  question('4.10', '是否有防蟲鼠管制與定期紀錄？'),
  question('4.11', '是否具有清潔標準SOP及相關紀錄？'),
  question('4.12', '拒用品是否皆有實體隔離及管制SOP？'),
  question('4.13', '是否使用電腦化系統？'),
  question('4.14', '商品進出貨是否保存完整紀錄且可以追蹤流向？'),
  question('5.1', '藥品運輸過程是否依照儲存條件運送？', 'TRANSPORT'),
  question('5.2', '藥品運送方式是否為溫控車並完整紀錄？', 'TRANSPORT'),
  question('5.3', '是否有GPS定位系統？', 'TEMPERATURE_VEHICLE_YES'),
  question('5.4', '冷鏈溫控包裝是否皆已經確效並完整紀錄？', 'COLD_CHAIN_WITHOUT_VEHICLE'),
  question('5.5', '共用車輛時，是否有清潔標準程序，確保不會危及藥品品質完整性？', 'TRANSPORT'),
  question('6.1', '是否同意簽訂「品質協議」？', 'ALL'),
  question(
    '6.2',
    '如需將雙方合作事項委託給第三方，是否會提前告知並經惠生評估同意後才執行？',
    'ALL',
  ),
  question('6.3', '藥品銷毀是否有完整紀錄？'),
  question('6.4', '人員是否持有相關證照？', 'PHARMACEUTICAL', 'PERSONNEL_CERTIFICATE', true),
];

const optionalText = z.string().trim().max(2000).default('');
const date = z
  .string()
  .refine(
    (v) =>
      !v ||
      (/^\d{4}-\d{2}-\d{2}$/.test(v) &&
        Number.isFinite(Date.parse(v)) &&
        new Date(v).toISOString().slice(0, 10) === v),
    '請輸入有效日期。',
  )
  .default('');
const uniqueArray = <T extends z.ZodTypeAny>(item: T) =>
  z
    .array(item)
    .max(30)
    .refine((v) => new Set(v).size === v.length, '選項不可重複。')
    .default([]);
export const supplierQualificationSchema = z
  .object({
    responsible_person: z.string().trim().max(80).default(''),
    contact_line: z.string().trim().max(120).default(''),
    supplier_qualification_types: uniqueArray(
      z.enum(['DRUG_DEALER', 'DRUG_MANUFACTURER', 'TRANSPORTER', 'GENERAL_SUPPLIER', 'OTHER']),
    ),
    qualification_other: optionalText,
    drug_dealer_license_number: z.string().trim().max(120).default(''),
    drug_manufacturer_license_number: z.string().trim().max(120).default(''),
    gdp_status: z.enum(['', 'COMPLETED', 'NOT_COMPLETED', 'NOT_APPLICABLE']).default(''),
    gdp_issue_date: date,
    gdp_expiry_date: date,
    gdp_approved_scopes: uniqueArray(z.enum(['STORAGE', 'TRANSPORT', 'OTHER'])),
    has_pharmaceutical_transport: z.boolean().default(false),
    transport_responsibility: z.enum(['', 'SUPPLIER', 'THIRD_PARTY', 'OTHER']).default(''),
    transport_company_name: z.string().trim().max(160).default(''),
    transport_temperature_conditions: uniqueArray(
      z.enum(['COLD_2_8', 'CONTROLLED_15_25', 'BELOW_25', 'BELOW_30', 'AMBIENT', 'OTHER']),
    ),
    transport_temperature_other: optionalText,
    transport_methods: uniqueArray(z.enum(['A', 'B', 'C', 'D', 'E', 'F'])),
    transport_notes: optionalText,
    warehouse_type: z.enum(['', 'OWN', 'OUTSOURCED', 'BOTH']).default(''),
    warehouse_company_name: z.string().trim().max(160).default(''),
    filled_by_name: z.string().trim().max(80).default(''),
    filled_by_title: z.string().trim().max(80).default(''),
    filled_by_email: z.union([z.literal(''), z.string().email('請輸入有效Email。')]).default(''),
    declaration_accepted: z.boolean().default(false),
  })
  .strict();
export type SupplierQualification = z.infer<typeof supplierQualificationSchema>;
export const supplierAnswerSchema = z
  .object({
    answer: z.enum(['YES', 'NO', 'NOT_APPLICABLE']).nullable().default(null),
    comment: optionalText,
    certification_name: z.string().trim().max(160).default(''),
    document_ids: uniqueArray(z.string().min(1).max(180)),
  })
  .strict();
export type SupplierAnswer = z.infer<typeof supplierAnswerSchema>;
export const supplierAssessmentDraftSchema = z
  .object({
    template_version: z.literal(SUPPLIER_ASSESSMENT_VERSION).default(SUPPLIER_ASSESSMENT_VERSION),
    qualification: supplierQualificationSchema.default({}),
    answers: z
      .record(supplierAnswerSchema)
      .default({})
      .superRefine((answers, ctx) => {
        for (const code of Object.keys(answers)) {
          if (!SUPPLIER_ASSESSMENT_QUESTIONS.some((q) => q.question_code === code))
            ctx.addIssue({ code: 'custom', path: [code], message: '未知問卷題號。' });
        }
      }),
    current_step: z.number().int().min(1).max(8).default(1),
  })
  .strict();
export type SupplierAssessmentDraft = z.infer<typeof supplierAssessmentDraftSchema>;
export const supplierAssessmentSaveSchema = z
  .object({
    revision: z.number().int().min(0),
    draft: supplierAssessmentDraftSchema,
  })
  .strict();
export interface SupplierDocument {
  id: string;
  supplier_application_id: string;
  document_type: SupplierDocumentType;
  issue_date: string;
  expiry_date: string;
  deleted_at?: string | null;
}
export interface SupplierDocumentView extends SupplierDocument {
  file_name: string;
  original_name?: string;
  content_type: string;
  size_bytes: number;
  uploaded_at: string;
  status: 'VALID' | 'EXPIRING_SOON' | 'EXPIRED' | 'UNKNOWN';
}
export interface SupplierAssessmentState {
  draft: SupplierAssessmentDraft;
  revision: number;
  updated_at: string | null;
  documents: SupplierDocumentView[];
  evaluation: ReturnType<typeof evaluateSupplierAssessment>;
}
export const supplierDocumentMetadataSchema = z
  .object({
    document_type: z.enum(
      Object.keys(DOCUMENT_LABELS) as [SupplierDocumentType, ...SupplierDocumentType[]],
    ),
    issue_date: date,
    expiry_date: date,
  })
  .strict()
  .refine(
    (value) => !value.issue_date || !value.expiry_date || value.expiry_date >= value.issue_date,
    '有效日期不得早於發證日期。',
  );
export interface AssessmentIssue {
  path: string;
  step: number;
  message: string;
}
export interface AssessmentPolicy {
  companySealRequired: boolean;
  blockMissingQualification: boolean;
}
export const DEFAULT_ASSESSMENT_POLICY: AssessmentPolicy = {
  companySealRequired: false,
  blockMissingQualification: false,
};

export class SupplierAssessmentApplicabilityService {
  constructor(
    readonly qualification: SupplierQualification,
    readonly supplyCategories: readonly string[],
    readonly answers: Record<string, SupplierAnswer>,
  ) {}
  get pharmaceutical(): boolean {
    return (
      this.qualification.supplier_qualification_types.some(
        (v) => v === 'DRUG_DEALER' || v === 'DRUG_MANUFACTURER',
      ) ||
      this.supplyCategories.some((v) => ['A', 'B', 'C', 'D', 'E', 'F', 'G'].includes(v)) ||
      this.transport
    );
  }
  get transport(): boolean {
    return (
      this.qualification.has_pharmaceutical_transport ||
      (this.qualification.gdp_status === 'COMPLETED' &&
        this.qualification.gdp_approved_scopes.includes('TRANSPORT'))
    );
  }
  section(section: AssessmentSection): Applicability {
    if (section === '1' || section === '6') return 'REQUIRED';
    if (section === '5') return this.transport ? 'REQUIRED' : 'NOT_APPLICABLE';
    return this.pharmaceutical ? 'REQUIRED' : 'NOT_APPLICABLE';
  }
  question(q: AssessmentQuestion): Applicability {
    if (q.applicability_rule === 'ALL') return 'REQUIRED';
    if (this.section(q.section_code) === 'NOT_APPLICABLE' || !this.pharmaceutical)
      return 'NOT_APPLICABLE';
    if (q.applicability_rule === 'MANUFACTURER')
      return this.qualification.supplier_qualification_types.includes('DRUG_MANUFACTURER')
        ? 'REQUIRED'
        : 'OPTIONAL';
    if (q.applicability_rule === 'CALIBRATION_YES' && this.answers['4.7']?.answer !== 'YES')
      return 'NOT_APPLICABLE';
    if (q.applicability_rule === 'TEMPERATURE_VEHICLE_YES' && this.answers['5.2']?.answer !== 'YES')
      return 'NOT_APPLICABLE';
    if (
      q.applicability_rule === 'COLD_CHAIN_WITHOUT_VEHICLE' &&
      (this.answers['5.2']?.answer !== 'NO' ||
        !this.qualification.transport_temperature_conditions.includes('COLD_2_8'))
    )
      return 'NOT_APPLICABLE';
    return 'REQUIRED';
  }
}

/** Hidden answers are excluded from persisted data, completion and submission history. */
export function normalizeSupplierAssessment(
  draft: SupplierAssessmentDraft,
  supplyCategories: readonly string[],
): SupplierAssessmentDraft {
  const rules = new SupplierAssessmentApplicabilityService(
    draft.qualification,
    supplyCategories,
    draft.answers,
  );
  return {
    ...draft,
    answers: Object.fromEntries(
      SUPPLIER_ASSESSMENT_QUESTIONS.filter(
        (q) => rules.question(q) !== 'NOT_APPLICABLE' && draft.answers[q.question_code],
      ).map((q) => [q.question_code, draft.answers[q.question_code]]),
    ),
  };
}

export function documentValidity(
  expiryDate: string,
  today: string,
  warningDays = 30,
): 'VALID' | 'EXPIRING_SOON' | 'EXPIRED' | 'UNKNOWN' {
  if (
    !expiryDate ||
    !date.safeParse(expiryDate).success ||
    !date.safeParse(today).success ||
    !today
  )
    return 'UNKNOWN';
  const days = (Date.parse(expiryDate) - Date.parse(today)) / 86400000;
  return days < 0 ? 'EXPIRED' : days <= warningDays ? 'EXPIRING_SOON' : 'VALID';
}

// 前後端共用；documents 必須由後端依目前 Session 所屬申請讀取，不信任 Request 的文件中繼資料。
export function evaluateSupplierAssessment(
  draft: SupplierAssessmentDraft,
  applicationId: string,
  supplyCategories: readonly string[],
  documents: readonly SupplierDocument[],
  policy: AssessmentPolicy = DEFAULT_ASSESSMENT_POLICY,
) {
  const q = draft.qualification;
  const rules = new SupplierAssessmentApplicabilityService(q, supplyCategories, draft.answers);
  const issues: AssessmentIssue[] = [];
  const warnings: AssessmentIssue[] = [];
  let required = 0;
  let completed = 0;
  const check = (ok: boolean, path: string, step: number, message: string) => {
    required += 1;
    if (ok) completed += 1;
    else issues.push({ path, step, message });
  };
  const validDocs = documents.filter(
    (d) => d.supplier_application_id === applicationId && !d.deleted_at,
  );
  // Check every reference, including hidden answers, before normalization discards stale fields.
  for (const [code, response] of Object.entries(draft.answers)) {
    for (const documentId of response.document_ids) {
      if (!validDocs.some((doc) => doc.id === documentId))
        check(false, `answers.${code}.document_ids`, 5, `${code}附件不屬於本申請或已移除。`);
    }
  }
  const requiredDocuments = new Map<SupplierDocumentType, { step: number; path: string }>();
  check(Boolean(q.responsible_person), 'qualification.responsible_person', 1, '請填寫公司負責人。');
  check(
    q.supplier_qualification_types.length > 0,
    'qualification.supplier_qualification_types',
    2,
    '請選擇供應商資格類型。',
  );
  if (q.supplier_qualification_types.includes('OTHER'))
    check(
      Boolean(q.qualification_other),
      'qualification.qualification_other',
      2,
      '請說明其他供應商資格。',
    );
  for (const [type, field, doc] of [
    ['DRUG_DEALER', 'drug_dealer_license_number', 'DRUG_DEALER_LICENSE'],
    ['DRUG_MANUFACTURER', 'drug_manufacturer_license_number', 'DRUG_MANUFACTURER_LICENSE'],
  ] as const) {
    if (q.supplier_qualification_types.includes(type)) {
      check(Boolean(q[field]), `qualification.${field}`, 2, `請填寫${DOCUMENT_LABELS[doc]}字號。`);
      requiredDocuments.set(doc, { step: 2, path: `documents.${doc}` });
    }
  }
  if (rules.pharmaceutical) {
    check(Boolean(q.gdp_status), 'qualification.gdp_status', 2, '請選擇GDP狀態。');
    check(Boolean(q.warehouse_type), 'qualification.warehouse_type', 5, '請選擇倉儲方式。');
    if (q.warehouse_type === 'OUTSOURCED' || q.warehouse_type === 'BOTH')
      check(
        Boolean(q.warehouse_company_name),
        'qualification.warehouse_company_name',
        5,
        '請填寫委外倉儲公司名稱。',
      );
  }
  if (q.gdp_status === 'COMPLETED')
    requiredDocuments.set('GDP_APPROVAL', { step: 2, path: 'documents.GDP_APPROVAL' });
  if (q.gdp_issue_date && q.gdp_expiry_date)
    check(
      q.gdp_expiry_date >= q.gdp_issue_date,
      'qualification.gdp_expiry_date',
      2,
      'GDP有效日期不得早於發證日期。',
    );
  if (rules.pharmaceutical || q.supplier_qualification_types.includes('TRANSPORTER')) {
    check(
      Boolean(q.transport_responsibility),
      'qualification.transport_responsibility',
      4,
      '請選擇運輸責任者。',
    );
    if (q.transport_responsibility === 'THIRD_PARTY' || q.transport_responsibility === 'OTHER')
      check(
        Boolean(q.transport_company_name),
        'qualification.transport_company_name',
        4,
        '請填寫配送運輸業者名稱。',
      );
    if (q.transport_temperature_conditions.includes('OTHER'))
      check(
        Boolean(q.transport_temperature_other),
        'qualification.transport_temperature_other',
        4,
        '請說明其他運送溫度。',
      );
    if (q.transport_methods.includes('F'))
      check(Boolean(q.transport_notes), 'qualification.transport_notes', 4, '請說明其他運輸方式。');
  }
  const sections = Object.fromEntries(
    Object.keys(ASSESSMENT_SECTIONS).map((s) => [
      s,
      {
        applicability: rules.section(s as AssessmentSection),
        required: 0,
        completed: 0,
        yes: 0,
        no: 0,
        notApplicable: 0,
      },
    ]),
  ) as Record<
    AssessmentSection,
    {
      applicability: Applicability;
      required: number;
      completed: number;
      yes: number;
      no: number;
      notApplicable: number;
    }
  >;
  for (const item of SUPPLIER_ASSESSMENT_QUESTIONS) {
    const applicability = rules.question(item);
    if (applicability === 'NOT_APPLICABLE') continue;
    const response = draft.answers[item.question_code];
    const answer = response?.answer;
    const section = sections[item.section_code];
    if (applicability === 'REQUIRED') {
      section.required += 1;
      if (answer) section.completed += 1;
      check(
        Boolean(answer),
        `answers.${item.question_code}`,
        5,
        `請回答${item.question_code} ${item.question_text}`,
      );
    }
    if (answer === 'YES') {
      section.yes += 1;
      if (item.required_document_type && !requiredDocuments.has(item.required_document_type))
        requiredDocuments.set(item.required_document_type, {
          step: 5,
          path: `answers.${item.question_code}`,
        });
      if (item.requires_description) {
        check(
          Boolean(response.certification_name),
          `answers.${item.question_code}.certification_name`,
          5,
          `請填寫${item.question_code}證照／認證名稱。`,
        );
        check(
          Boolean(response.comment),
          `answers.${item.question_code}.comment`,
          5,
          `請填寫${item.question_code}證照／認證說明。`,
        );
      }
    } else if (answer === 'NO') {
      section.no += 1;
      const warning = {
        path: `answers.${item.question_code}`,
        step: 5,
        message:
          item.section_code === '1'
            ? `${item.question_code} 必要資格尚未符合。`
            : `${item.question_code} 回答否，需採購注意：${item.question_text}`,
      };
      warnings.push(warning);
      if (policy.blockMissingQualification && item.section_code === '1')
        check(false, warning.path, 5, warning.message);
    } else if (answer === 'NOT_APPLICABLE') section.notApplicable += 1;
  }
  if (policy.companySealRequired)
    requiredDocuments.set('COMPANY_SEAL', { step: 7, path: 'documents.COMPANY_SEAL' });
  for (const [type, location] of requiredDocuments) {
    check(
      validDocs.some((d) => d.document_type === type),
      location.path,
      location.step,
      `請上傳${DOCUMENT_LABELS[type]}。`,
    );
  }
  check(Boolean(q.filled_by_name), 'qualification.filled_by_name', 7, '請填寫填表人姓名。');
  check(
    q.declaration_accepted,
    'qualification.declaration_accepted',
    7,
    '請勾選資料真實且完整聲明。',
  );
  return {
    issues,
    warnings,
    sections,
    required,
    completed,
    percentage: required ? Math.round((completed / required) * 100) : 100,
    requiredDocumentTypes: [...requiredDocuments.keys()],
    // 既有公司、聯絡、財務資料的完成度由既有 validator 合併，不假裝此百分比涵蓋整份申請。
  };
}

export const supplierAssessmentCategorySchema = z.array(z.enum(VENDOR_PRODUCT_CATEGORY_CODES));
